import Button from "@components/Button"
import Input from "@components/Input"
import FunnelHeader from "@components/quiz/FunnelHeader"
import FunnelLayout from "@components/quiz/FunnelLayout"
import StickyFooter from "@components/quiz/StickyFooter"
import StatusIcon from "@components/StatusIcon"
import { canGoBack } from "@helpers/history"
import { resetPasswordForEmail } from "@helpers/supabase"
import { validateEmail } from "@helpers/validations"
import { EnvelopeIcon } from "@heroicons/react/24/outline"
import { useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Link, useNavigate } from "react-router-dom"

const SIGN_IN_PATH = "/account/sign-in"

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("")
  const [isEmailSent, setIsEmailSent] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const navigate = useNavigate()
  const { t } = useTranslation()

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    setIsSubmitting(true)

    await resetPasswordForEmail({ email })

    setIsSubmitting(false)
    setIsEmailSent(true)
  }

  const handleBack = () => {
    if (canGoBack()) navigate(-1)
    else navigate(SIGN_IN_PATH)
  }

  return (
    <FunnelLayout header={<FunnelHeader onBack={handleBack} />}>
      {isEmailSent ? (
        <div
          aria-live="polite"
          className="flex grow animate-fade-up flex-col gap-6 pb-8 pt-6"
        >
          <StatusIcon icon={EnvelopeIcon} tone="primary" />

          <div className="space-y-3">
            <h1 className="vm-heading">
              {t("pages.account.forgotPassword.dialog.title")}
            </h1>

            <p className="vm-subheading">
              <Trans
                components={{
                  bold: <span className="font-semibold text-vm-ink" />,
                }}
                i18nKey="pages.account.forgotPassword.dialog.text"
              />
            </p>
          </div>

          <StickyFooter>
            <Link className="vm-button-secondary" to={SIGN_IN_PATH}>
              {t("pages.account.forgotPassword.backToSignIn")}
            </Link>
          </StickyFooter>
        </div>
      ) : (
        <form
          className="flex grow animate-slide-in-right flex-col gap-6 pt-4"
          onSubmit={(event) => void handleSubmit(event)}
        >
          <div className="animate-fade-up space-y-3">
            <h1 className="vm-heading">
              {t("pages.account.forgotPassword.heading")}
            </h1>

            <p className="vm-subheading">
              {t("pages.account.forgotPassword.enterEmail")}
            </p>
          </div>

          <div className="animate-fade-up [animation-delay:120ms]">
            <Input
              autoComplete="email"
              autoFocus
              disabled={isSubmitting}
              inputMode="email"
              onChange={(event) => setEmail(event.target.value)}
              placeholder={t("shared.email")}
              type="email"
              value={email}
            />
          </div>

          <StickyFooter>
            <Button
              disabled={!validateEmail(email)}
              submitting={isSubmitting}
              title={t("pages.account.forgotPassword.resetPassword")}
              type="submit"
            />
          </StickyFooter>
        </form>
      )}
    </FunnelLayout>
  )
}
